import { getErrorMessage } from 'utils/error-message'
import { CompetitionPayload } from 'models/endpoints-payload'
import { FormElements } from '../models/form-elements'
import { t9n } from 't9n/t9n'
import {
  checkIfPayloadComplete,
  checkValidity,
  consentValidator,
  emailValidator,
  getBooleanField,
  getStringField,
  resetAllErrorMSG,
  setErrorMSG,
} from './form-handling'
import competitionSubscribe from 'services/competition-subscribe'
import { isNotEmptyTrimmedStringValidator } from '@currency-one/validators'
import sendMailToNewsletter from './newsletter'
import { getCurrentLanguage } from '../utils/current-language'
import { modalHandle } from 'src/assets/scripts/utils/dialog'

type FormContainer = Document | HTMLElement
type RateValidator = (value: string, selector: string, formContainer: FormContainer) => string
const currentLanguage = getCurrentLanguage()

export const getElementsForm = (): FormElements | null => {
  const $formContainerParent = document.querySelector<HTMLDivElement>('[data-collect-form]')
  if (!$formContainerParent) {
    console.warn('Form container not found')
    return null
  }

  const formElements = {
    $formContainerParent,
    $emailField: $formContainerParent.querySelector<HTMLInputElement>('[data-email-field]'),
    $errorEmailField: $formContainerParent.querySelector<HTMLSpanElement>('[data-error-field]'),
    $inputElementHolder: $formContainerParent.querySelector<HTMLDivElement>('[data-input-holder]'),
    $endView: $formContainerParent.querySelector<HTMLDivElement>('[data-end-view]'),
    $sendError: $formContainerParent.querySelector<HTMLParagraphElement>('[data-error-server]'),
    $eventInputElement: $formContainerParent.querySelector<HTMLInputElement>('[data-competition]'),
    $answerTextareaField: $formContainerParent.querySelector<HTMLTextAreaElement>('[data-answer]'),
    $errorAnswerField: $formContainerParent.querySelector<HTMLSpanElement>('[data-error-field-answer]'),
    $checkboxElement: $formContainerParent.querySelector<HTMLInputElement>('[data-checkbox-field="terms"]'),
    $submitFormBtn: $formContainerParent.querySelector<HTMLButtonElement>('[data-submit-form]'),
    $rodo: $formContainerParent.querySelector<HTMLInputElement>('[data-newsletter-rodo]'),
    $modal: document.querySelector<HTMLDialogElement>('[data-modal]'),
  }

  return formElements
}

// Function fetches data from form and create payload object.
export const getFormData = (formElements: FormElements): CompetitionPayload => {
  return {
    email: getStringField('[data-email-field]', emailValidator, formElements.$formContainerParent),
    event: formElements.$eventInputElement.value || undefined,
    answer: getStringField('[data-answer]', stringValidator, formElements.$formContainerParent) || '',
    date: new Date().toISOString(),
    terms_and_conditions_accepted: getBooleanField(
      '[data-checkbox-field="terms"]',
      consentValidator,
      formElements.$formContainerParent,
    ),
  }
}

// Validator for rate inputs field
const rateValidator = (value: string, selector: string, formContainer: FormContainer = document): string | null => {
  const isValid = [!value ? t9n('formValidation.rateEmpty', currentLanguage) : '']
  return checkValidity(isValid, selector, formContainer) ? value : null
}

const getRate = (selector: string, validator: RateValidator, formContainer: FormContainer = document): string => {
  const $inputs = formContainer.querySelectorAll<HTMLInputElement>(selector)
  const $numbers = []

  $inputs.forEach(($input, key) => {
    validator(String($input.value).trim(), selector, formContainer)
    $numbers.push(key === 0 ? `${$input.value},` : $input.value)
  })

  return $numbers.join('')
}

const prepareAnswer = (formElements: FormElements): void => {
  const rate = getRate('[data-rate]', rateValidator, formElements.$formContainerParent)
  formElements.$answerTextareaField.value = rate && rate.length === 6 ? rate : null
}

const handleRatesInput = (selector: string, formContainer: FormContainer = document): void => {
  const $rates = formContainer.querySelectorAll<HTMLInputElement>(selector)
  $rates.forEach(($rate, key) => {
    $rate.addEventListener('input', () => {
      $rate.value = $rate.value.replace(/[^0-9]/g, '')
      if (key + 1 < $rates.length && $rate.value) $rates[key + 1].focus()
      if (key - 1 >= 0 && $rate.value === '') $rates[key - 1].focus()
    })
  })
}

const stringValidator = (value: string, selector: string, formContainer: FormContainer = document): string | null => {
  const isValid = [!isNotEmptyTrimmedStringValidator(value) ? t9n('formValidation.emptyField', currentLanguage) : '']
  return checkValidity(isValid, selector, formContainer) ? value : null
}

const validateNewsletterConsent = (
  isConsentRequired: boolean,
  rodoElement: HTMLInputElement | null | undefined,
  formContainer: HTMLElement,
): boolean => {
  if (!isConsentRequired || !rodoElement) return true

  const rodoValid = consentValidator(rodoElement.checked, '[data-newsletter-rodo]', formContainer)

  return rodoValid
}

export const collectDataToSend = (
  formElements: FormElements,
  isRate: boolean = false,
  isNewsletterConsentRequired: boolean = false,
): void => {
  resetAllErrorMSG(formElements.$formContainerParent)
  if (isRate) prepareAnswer(formElements)

  const payload = getFormData(formElements)

  const isPayloadComplete: boolean = checkIfPayloadComplete(payload)
  const isNewsletterConsentValid = validateNewsletterConsent(
    isNewsletterConsentRequired,
    formElements.$rodo,
    formElements.$formContainerParent,
  )
  const isValid: boolean = isPayloadComplete && isNewsletterConsentValid

  if (isValid) {
    if (formElements.$rodo?.checked) sendMailToNewsletter(formElements)
    competitionSubscribe(payload)
      .then(() => {
        formElements.$modal
          ? formElements.$modal.showModal()
          : ((formElements.$inputElementHolder.hidden = true), (formElements.$endView.hidden = false))
        clearForm()
      })
      .catch((error: unknown) => {
        console.error(getErrorMessage(error))
        setErrorMSG(
          '[data-error-server]',
          t9n('formValidation.serverUnknownError', currentLanguage),
          formElements.$formContainerParent,
        )
      })
  } else {
    console.warn('Payload is not complete or newsletter consent is missing')
  }
}

const clearForm = (formContainer: FormContainer = document): void => {
  const inputs = formContainer.querySelectorAll<HTMLInputElement>('input:not([data-competition])')
  const textareas = formContainer.querySelectorAll<HTMLTextAreaElement>('textarea')

  inputs.forEach((input) => {
    input.type !== 'checkbox' ? (input.value = '') : (input.checked = false)
  })

  textareas.forEach((textarea) => {
    textarea.value = ''
  })
}

const initForm = (isNewsletterConsentRequired: boolean = false): void => {
  const isRate = !!document.querySelector<HTMLInputElement>('[data-rate]')
  const formElements = getElementsForm()

  formElements.$submitFormBtn?.addEventListener('click', () =>
    collectDataToSend(formElements, isRate, isNewsletterConsentRequired),
  )

  if (isRate) handleRatesInput('[data-rate]', formElements.$formContainerParent)
  if (formElements.$modal) modalHandle(formElements.$modal)
}

export default initForm
